import * as React from "react";
import { Calendar, Loader2, TrendingUp, TrendingDown, Flame, Snowflake } from "lucide-react";
import {
  Card,
  CardHeader,
  CardContent,
  CardTitle,
} from "./card";
import { PercentValue, CurrencyValue } from "./value-display";
import { Text, MonoText } from "./typography";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip";

/**
 * Get color for heatmap cell based on percentage change
 * Uses teal for gains (matching app accent) and red for losses
 */
function getHeatmapColor(changePercent) {
  if (changePercent === null || changePercent === undefined) {
    return "#374151"; // gray-700 - no data
  }

  // Neutral threshold
  if (Math.abs(changePercent) < 0.01) {
    return "#4b5563"; // gray-600 - neutral
  }

  // Gains - teal shades
  if (changePercent > 0) {
    if (changePercent >= 3) return "#2dd4bf"; // teal-400 - strong gain
    if (changePercent >= 1) return "#14b8a6"; // teal-500 - medium gain
    return "#0d9488"; // teal-600 - light gain
  }

  // Losses - red shades
  if (changePercent <= -3) return "#dc2626"; // red-600 - strong loss
  if (changePercent <= -1) return "#ef4444"; // red-500 - medium loss
  return "#f87171"; // red-400 - light loss
}

/**
 * Organize heatmap data by week for GitHub-style grid
 * Returns array of weeks, where each week has 7 slots (Sun-Sat)
 * Also returns month label positions for the header
 */
function organizeByWeek(heatmapData) {
  if (!heatmapData || heatmapData.length === 0) return { weeks: [], monthLabels: [] };

  const weeks = [];
  let currentWeek = [];
  const monthLabels = [];
  let lastMonthSeen = -1;

  // Sort by date
  const sortedData = [...heatmapData].sort(
    (a, b) => new Date(a.date) - new Date(b.date)
  );

  // Get the first date's day of week (0 = Sunday)
  const firstDate = new Date(sortedData[0].date);
  const firstDayOfWeek = firstDate.getDay();

  // Add empty cells for days before the first data point
  for (let i = 0; i < firstDayOfWeek; i++) {
    currentWeek.push(null);
  }

  sortedData.forEach((day) => {
    const date = new Date(day.date);
    const dayOfWeek = date.getDay();
    const month = date.getMonth();

    // Start a new week on Sunday
    if (dayOfWeek === 0 && currentWeek.length > 0) {
      weeks.push(currentWeek);
      currentWeek = [];
    }

    // Track month labels - add label at first occurrence of new month
    if (month !== lastMonthSeen) {
      const weekIndex = weeks.length; // Current week index where month starts
      monthLabels.push({
        label: date.toLocaleDateString("en-US", { month: "short" }),
        weekIndex,
      });
      lastMonthSeen = month;
    }

    currentWeek.push(day);
  });

  // Push the last week if it has data
  if (currentWeek.length > 0) {
    weeks.push(currentWeek);
  }

  return { weeks, monthLabels };
}

/**
 * PerformanceHeatmap Component
 * GitHub-style calendar showing daily portfolio performance
 *
 * @param {Object} props
 * @param {Object} props.data - Heatmap data from API
 * @param {boolean} props.loading - Loading state
 */
export function PerformanceHeatmap({ data, loading = false }) {
  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2 pt-3 px-4">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-primary" />
            <CardTitle className="text-sm font-medium">Performance Heatmap</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-3">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.heatmapData || data.heatmapData.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2 pt-3 px-4">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-primary" />
            <CardTitle className="text-sm font-medium">Performance Heatmap</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-3">
          <Text variant="secondary" size="sm" className="text-center py-8">
            Need more snapshot data to generate heatmap. Keep tracking your portfolio daily!
          </Text>
        </CardContent>
      </Card>
    );
  }

  const { heatmapData, stats } = data;
  const { weeks, monthLabels } = organizeByWeek(heatmapData);

  // Day labels for left side (GitHub shows Mon, Wed, Fri)
  const dayLabels = [
    { day: 0, label: "" },      // Sun - hidden
    { day: 1, label: "Mon" },   // Mon - visible
    { day: 2, label: "" },      // Tue - hidden
    { day: 3, label: "Wed" },   // Wed - visible
    { day: 4, label: "" },      // Thu - hidden
    { day: 5, label: "Fri" },   // Fri - visible
    { day: 6, label: "" },      // Sat - hidden
  ];

  return (
    <Card>
      <CardHeader className="pb-2 pt-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-primary" />
            <CardTitle className="text-sm font-medium">Performance Heatmap</CardTitle>
          </div>
          <Text variant="secondary" size="xs">
            {stats.totalDays} trading days
          </Text>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-3 space-y-4">
        {/* Stats summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="flex items-center gap-2 p-2 rounded-md bg-success/10">
            <TrendingUp className="h-4 w-4 text-success" />
            <div>
              <MonoText size="sm" className="font-semibold text-success">
                {stats.positiveDays}
              </MonoText>
              <Text variant="secondary" size="xs">Green days</Text>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 rounded-md bg-destructive/10">
            <TrendingDown className="h-4 w-4 text-destructive" />
            <div>
              <MonoText size="sm" className="font-semibold text-destructive">
                {stats.negativeDays}
              </MonoText>
              <Text variant="secondary" size="xs">Red days</Text>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 rounded-md bg-success/10">
            <Flame className="h-4 w-4 text-success" />
            <div>
              <MonoText size="sm" className="font-semibold text-success">
                {stats.longestWinStreak}
              </MonoText>
              <Text variant="secondary" size="xs">Win streak</Text>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 rounded-md bg-destructive/10">
            <Snowflake className="h-4 w-4 text-destructive" />
            <div>
              <MonoText size="sm" className="font-semibold text-destructive">
                {stats.longestLoseStreak}
              </MonoText>
              <Text variant="secondary" size="xs">Lose streak</Text>
            </div>
          </div>
        </div>

        {/* Heatmap grid - GitHub style layout */}
        <div className="overflow-x-auto">
          <TooltipProvider>
            <div className="inline-flex flex-col gap-1 min-w-fit">
              {/* Month labels row - positioned above the grid */}
              <div className="flex h-4 mb-1">
                <div className="w-8 flex-shrink-0" /> {/* Spacer for day labels column */}
                <div className="relative" style={{ width: `${weeks.length * 14}px` }}>
                  {monthLabels.map((month, index) => (
                    <Text
                      key={`${month.label}-${index}`}
                      variant="secondary"
                      size="xs"
                      className="absolute top-0 text-[10px] whitespace-nowrap"
                      style={{ left: `${month.weekIndex * 14}px` }}
                    >
                      {month.label}
                    </Text>
                  ))}
                </div>
              </div>

              {/* Main grid: day labels on left + week columns */}
              <div className="flex">
                {/* Day labels column (vertical, on left) */}
                <div className="flex flex-col gap-0.5 mr-1 w-7 flex-shrink-0">
                  {dayLabels.map(({ day, label }) => (
                    <div
                      key={day}
                      className="w-full h-3 flex items-center justify-end pr-1"
                    >
                      {label && (
                        <Text variant="secondary" size="xs" className="text-[9px] leading-none">
                          {label}
                        </Text>
                      )}
                    </div>
                  ))}
                </div>

                {/* Week columns */}
                <div className="flex gap-0.5">
                  {weeks.map((week, weekIndex) => (
                    <div key={weekIndex} className="flex flex-col gap-0.5">
                      {/* Pad week to always have 7 days (Sun-Sat) */}
                      {Array(7).fill(null).map((_, dayIndex) => {
                        const day = week[dayIndex];

                        if (!day) {
                          return (
                            <div
                              key={`empty-${weekIndex}-${dayIndex}`}
                              className="w-3 h-3 rounded-sm"
                              style={{ backgroundColor: "#1f2937" }} // gray-800 - empty
                            />
                          );
                        }

                        const color = getHeatmapColor(day.changePercent);
                        const dateFormatted = new Date(day.date).toLocaleDateString("en-US", {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        });

                        return (
                          <Tooltip key={day.date}>
                            <TooltipTrigger asChild>
                              <div
                                className="w-3 h-3 rounded-sm cursor-pointer transition-transform hover:scale-125 hover:z-10"
                                style={{ backgroundColor: color }}
                              />
                            </TooltipTrigger>
                            <TooltipContent side="top" className="p-2">
                              <div className="space-y-1">
                                <Text size="xs" className="font-medium">
                                  {dateFormatted}
                                </Text>
                                <div className="flex items-center gap-2">
                                  <PercentValue
                                    value={day.changePercent}
                                    size="xs"
                                    showTrend
                                  />
                                  <CurrencyValue
                                    value={day.changeAmount}
                                    size="xs"
                                    colorize
                                    showSign
                                  />
                                </div>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TooltipProvider>
        </div>

        {/* Legend - positioned below grid with clear separation */}
        <div className="flex items-center justify-end gap-2 pt-4 mt-2 border-t border-border/50">
          <Text variant="secondary" size="xs">Losses</Text>
          <div className="flex gap-0.5">
            <div
              className="w-2.5 h-2.5 rounded-sm"
              style={{ backgroundColor: "#dc2626" }}
              title="Strong loss (< -3%)"
            />
            <div
              className="w-2.5 h-2.5 rounded-sm"
              style={{ backgroundColor: "#ef4444" }}
              title="Medium loss (-3% to -1%)"
            />
            <div
              className="w-2.5 h-2.5 rounded-sm"
              style={{ backgroundColor: "#f87171" }}
              title="Light loss (-1% to 0%)"
            />
            <div
              className="w-2.5 h-2.5 rounded-sm"
              style={{ backgroundColor: "#4b5563" }}
              title="Neutral (0%)"
            />
            <div
              className="w-2.5 h-2.5 rounded-sm"
              style={{ backgroundColor: "#0d9488" }}
              title="Light gain (0% to 1%)"
            />
            <div
              className="w-2.5 h-2.5 rounded-sm"
              style={{ backgroundColor: "#14b8a6" }}
              title="Medium gain (1% to 3%)"
            />
            <div
              className="w-2.5 h-2.5 rounded-sm"
              style={{ backgroundColor: "#2dd4bf" }}
              title="Strong gain (> 3%)"
            />
          </div>
          <Text variant="secondary" size="xs">Gains</Text>
        </div>

        {/* Best/Worst day */}
        {(stats.bestDay || stats.worstDay) && (
          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
            {stats.bestDay && (
              <div className="p-2 rounded-md bg-success/10">
                <Text variant="secondary" size="xs">Best Day</Text>
                <div className="flex items-center justify-between mt-1">
                  <Text size="xs" className="font-medium">
                    {new Date(stats.bestDay.date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
                  </Text>
                  <PercentValue
                    value={stats.bestDay.changePercent}
                    size="xs"
                    showTrend
                  />
                </div>
              </div>
            )}
            {stats.worstDay && (
              <div className="p-2 rounded-md bg-destructive/10">
                <Text variant="secondary" size="xs">Worst Day</Text>
                <div className="flex items-center justify-between mt-1">
                  <Text size="xs" className="font-medium">
                    {new Date(stats.worstDay.date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
                  </Text>
                  <PercentValue
                    value={stats.worstDay.changePercent}
                    size="xs"
                    showTrend
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default PerformanceHeatmap;
